import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import { Scope } from '@unform/core'
import { Alert, Search } from '@vtex/shoreline'
import { t } from 'i18next'

import Panel from '~/components/Panel'
import { FormGroup, Input, InputHidden, Label } from '~/components/unform'
import { getWhiteLabelPublisherId } from '~/helpers'
import { useAppInfo } from '~/hooks/useAppInfo'
import { useGetFetchQuery } from '~/hooks/useGetFetchQuery'
import useQueryParams from '~/hooks/useQuery'
import { getPublisherDestinationUrls } from '~/modules/retailMedia/store/industry/publisherConfiguration/actions'
import { useAppSelector } from '~/store/hooks'

import AlertBrands from '../../AlertBrands'
import { useCampaignForm } from '../hooks/useCampaignForm'
import { usePublishersConfigurations } from '../hooks/usePublisherConfigurations'
import OffsiteDestinationUrl from './OffsiteDestinationUrl'

type CampaignDestinationUrlProps = {
  // Single-varejista, single-brand only (confirmed by the author — FR-022l):
  // required only for the new offsite-with-channel experience, and only
  // when some media has no destination URL of its own to fall back to.
  // Onsite/legacy callers never pass this, so their behavior is unchanged.
  isRequired?: boolean
  handleSectionValidity?(isValid: boolean): void
  // Quando false, o painel começa recolhido e abre assim que passar a true.
  // Quem não informa mantém o painel aberto desde o início.
  unlocked?: boolean
}

const normalizeSearchValue = (value?: string) =>
  (value || '')
    .normalize('NFD')
    .replaceAll(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase()

const CampaignDestinationUrl = ({
  isRequired,
  handleSectionValidity,
  unlocked = true
}: CampaignDestinationUrlProps = {}) => {
  const publisherIdParam = useQueryParams().get('rmid')
  const [searchValue, setSearchValue] = useState('')

  const [destinationUrlValue, setDestinationUrlValue] = useState<string>()

  useEffect(() => {
    handleSectionValidity?.(!isRequired || !!destinationUrlValue)
  }, [isRequired, destinationUrlValue, handleSectionValidity])

  const { isWhiteLabel, isNetwork } = useAppInfo()
  const {
    selectedSubpublishers,
    campaignType,
    campaignPermissions,
    allowOffsiteBriefing
  } = useCampaignForm()

  const isOffsiteCampaign = campaignType === 'off_site'

  // The panel sits below the media on purpose (its required state is derived
  // from the accepted media), so whoever opens it first has no way of
  // knowing that filling it here spares them from filling every creative.
  // The media block states the same rule from the opposite side
  // (2026-09-15 demo, SPEC 004 item 5.2). Offsite-only: onsite has no
  // per-creative URL to fall back from. `allowOffsiteBriefing` is a publisher
  // permission and stays true while creating onsite at an offsite-enabled
  // publisher, so the note also keys on campaign type.
  const baseHelperText = t('rm:adsDestination.helperText')
  const allCreativesNote = t(
    'rm:campaign.create.CampaignDestinationUrl.helperText.allCreatives'
  )
  const panelDescription =
    allowOffsiteBriefing && isOffsiteCampaign
      ? `${baseHelperText} ${allCreativesNote}`
      : baseHelperText

  // The destination retailer changed — the value tracked above belonged to
  // whatever input was rendered for the previous retailer, so it no longer
  // reflects what's on screen now (review comment).
  useEffect(() => {
    setDestinationUrlValue(undefined)
  }, [selectedSubpublishers])

  // Reopens the panel when isRequired flips from false to true (FR-022l),
  // even if the operator had collapsed it before — but through controlled
  // `open` state instead of a remounting `key`, so the Unform input isn't
  // torn down and the value already typed isn't lost (review comment).
  const [isOpen, setIsOpen] = useState(unlocked)
  const wasRequiredRef = useRef(isRequired)

  useEffect(() => {
    if (unlocked) setIsOpen(true)
  }, [unlocked])

  // Still locked: the effect above opens it as soon as it unlocks.
  useEffect(() => {
    if (isRequired && !wasRequiredRef.current && unlocked) {
      setIsOpen(true)
    }
    wasRequiredRef.current = isRequired
  }, [isRequired, unlocked])

  const publisherId = useMemo(
    () => (isWhiteLabel ? getWhiteLabelPublisherId() : publisherIdParam),
    [isWhiteLabel, publisherIdParam]
  )

  const publisherInfo = useGetFetchQuery<GetPublisherResponse>([
    'getAccountInfo',
    publisherId
  ])

  const dispatch = useDispatch()

  const {
    industry: {
      publisherConfiguration: {
        listDestinationUrls: { data }
      }
    }
  } = useAppSelector(state => state.retailMedia)

  useEffect(() => {
    if (publisherId) {
      dispatch(getPublisherDestinationUrls(publisherId))
    }
  }, [dispatch, publisherId])

  const filteredDestinations = useMemo(() => {
    const destinations = data?.destinations || []

    // sort by name
    const sortedDestinations = [...destinations].sort((a, b) =>
      a.name.localeCompare(b.name)
    )

    if (!publisherInfo?.is_network || !selectedSubpublishers.length)
      return sortedDestinations

    return sortedDestinations.filter(d =>
      selectedSubpublishers.includes(d.publisher_id as PublisherId)
    )
  }, [data?.destinations, selectedSubpublishers, publisherInfo?.is_network])

  const isMultiDestination = data?.destinations?.length > 1
  // Search exists for network campaigns, where the destination list can be
  // large. A single publisher with a few brands (Casas Bahia / Extra / Ponto
  // Frio) does not get a search field.
  const isNetworkPublisher = !!publisherInfo?.is_network
  const hasDestinationSearch =
    isNetworkPublisher && filteredDestinations.length > 1
  const normalizedSearch = useMemo(
    () => normalizeSearchValue(searchValue.trim()),
    [searchValue]
  )
  const matchingDestinations = useMemo(
    () =>
      filteredDestinations.map(destination => {
        const searchableContent = [
          destination.name,
          destination.publisher_name,
          destination.code
        ]
          .map(normalizeSearchValue)
          .join(' ')

        return (
          !hasDestinationSearch ||
          !normalizedSearch ||
          searchableContent.includes(normalizedSearch)
        )
      }),
    [filteredDestinations, hasDestinationSearch, normalizedSearch]
  )
  const hasSearchResults = matchingDestinations.some(Boolean)
  const shouldShowEmptySearch =
    hasDestinationSearch && !!normalizedSearch && !hasSearchResults
  const handleSearchClear = useCallback(() => setSearchValue(''), [])

  // Fetch publisher configuration for each selected publisher from DestinationRetailer
  const { selectedPublishersConfigurations } = usePublishersConfigurations(
    selectedSubpublishers
  )

  const showSuggestionAlert =
    !!campaignPermissions?.require_subpublisher_approval

  const showCatalogDestinations =
    isMultiDestination || (showSuggestionAlert && isNetworkPublisher)

  // Empty catalog falls back to the single input too — the suggestion
  // alert above promises one even with no catalog entries yet (review
  // comment).
  const showSingleDestinationInput =
    (!showCatalogDestinations || filteredDestinations.length === 0) &&
    !isNetwork

  return (
    <Scope path="settings">
      <Panel
        open={isOpen}
        onChangeOpen={setIsOpen}
        title={t('rm:destinationsUrl')}
        description={panelDescription}
        isCollapsible
        noPaddingBody={hasDestinationSearch}
      >
        {showSuggestionAlert && (
          <Alert
            variant="informational"
            className={hasDestinationSearch ? 'mx-3 mt-3 mb-3' : 'mb-3'}
          >
            {t(
              'rm:campaign.create.CampaignDestinationUrl.info.definedBySubpublisher'
            )}
          </Alert>
        )}

        {isRequired && !destinationUrlValue && (
          <Alert variant="warning" className="mb-3">
            {t('rm:campaign.create.CampaignDestinationUrl.alerts.required')}
          </Alert>
        )}

        <FormGroup
          className={hasDestinationSearch ? 'px-3 pb-2 pt-2' : undefined}
        >
          {isOffsiteCampaign &&
            selectedPublishersConfigurations?.map(
              ({ publisherId, publisherName, config }) => (
                <OffsiteDestinationUrl
                  key={publisherId}
                  publisherId={publisherId}
                  publisherName={publisherName}
                  config={config}
                  required={isRequired}
                  onChange={setDestinationUrlValue}
                />
              )
            )}

          {!isOffsiteCampaign && (
            <>
              {data?.has_brands && !isNetworkPublisher && (
                <header className="mb-2">
                  <AlertBrands publisherId={publisherId} />
                </header>
              )}

              {hasDestinationSearch && (
                <Search
                  value={searchValue}
                  onChange={event => setSearchValue(event.target.value)}
                  onClear={handleSearchClear}
                  messages={{
                    placeholder: t(
                      'rm:campaign.destinationUrls.search.placeholder'
                    )
                  }}
                  className="mb-3"
                />
              )}

              {shouldShowEmptySearch && (
                <Alert variant="warning">
                  {t('rm:campaign.destinationUrls.search.notFound')}
                </Alert>
              )}

              {/* REFACToR BEFORE RELEASE - @brenonovelli */}
              {showSingleDestinationInput && (
                <Input name="destination_url" id="destination_url" />
              )}

              {showCatalogDestinations && filteredDestinations.length > 0 && (
                <Scope path="destinations">
                  {filteredDestinations.map((item, index) => (
                    <div
                      hidden={!matchingDestinations[index]}
                      data-destination-index={index}
                      key={`${item.publisher_id}-${item.code}`}
                    >
                      <Scope path={`${index}`}>
                        <FormGroup className="mb-2">
                          <Label text={item.name} htmlFor="url" />
                          <Input name="url" id="url" />
                          <InputHidden
                            id="brand"
                            name="brand"
                            value={item.code}
                          />
                          <InputHidden
                            id="publisher_id"
                            name="publisher_id"
                            value={item.publisher_id}
                          />
                        </FormGroup>
                      </Scope>
                    </div>
                  ))}
                </Scope>
              )}
            </>
          )}
        </FormGroup>
      </Panel>
    </Scope>
  )
}

export default CampaignDestinationUrl
